import React, { useRef, useEffect, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
    ClientPlatformCode,
    getImageName,
    ApiConfigData,
    GetImageUrl,
    handleRedirectWithLoading,
    ModalOrSso,
    SectionContentMasterData,
    trackEvent,
    UserIdAndStatusData,
    DefaultCardName,
} from "../../Utility/Utility";
import { useNavigate } from "react-router-dom";
import DisclaimerPopup from "../SupportingBanner/DisclaimerPopup";

const TopOfferBanner = ({
    isFullWidth,
    sectionId,
    setShowModal,
    login,
    setIsLoading,
    isOfferListPage,
    sectionData,
    setIsSupportBanner,
    isMobileView,
    isHomePage
}) => {
    const navigate = useNavigate();
    const { clientCode, platformCode } = ClientPlatformCode();
    const { userId, userStatus } = UserIdAndStatusData();
    const defaultCardName = DefaultCardName();
    const [disclaimerPopup, setDisclaimerPopup] = useState(false);
    const [bannerData, setBannerData] = useState(null);
    const { sctionContentdata } = SectionContentMasterData();
    const contentData = sctionContentdata.filter(
        (content) => content.sectionMasterId === sectionId
    );
    const sliderRef = useRef(null);
    const sectionRef = useRef(null);
    const activeSlideRef = useRef(0);
    const trackedSlideIndexesRef = useRef(new Set());
    const isModal = ModalOrSso();
    const apiConfigData = ApiConfigData();
    const integrationId = apiConfigData?.find(
        (data) => data?.paramsKey === "integrationId"
    )?.paramsValue;
    const loginUrl = `${process.env.REACT_APP_INTEGRATION_LOGIN_URL}Login/${integrationId}`;

    useEffect(() => {
        if (contentData?.length) {
            setIsSupportBanner(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentData?.length]);

    const eventTrackingOnView = (data) => {
        let bannerName = getImageName(data?.mobImage);
        trackEvent("view_promotion", {
            client_code: clientCode,
            platform_code: platformCode,
            user_id: userId,
            user_status: userStatus,
            card_name: defaultCardName,
            page_type: window.location.pathname === "/" ? "home_page" : "my_account_page",
            ecommerce: {
                creative_name: bannerName,
                creative_slot: `${sectionData?.displayOrder}-${data?.displayOrder}`,
                promotion_id: String(data?.id),
                promotion_name: sectionData?.text || sectionData?.sectionName
            }
        });
    };

    const isSectionInViewport = () => {
        if (!sectionRef.current) return false;
        const rect = sectionRef.current.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
    };

    const triggerViewEventForSlide = (slideIndex) => {
        if (!contentData?.length) return;

        const normalizedIndex =
            ((slideIndex % contentData.length) + contentData.length) % contentData.length;

        if (trackedSlideIndexesRef.current.has(normalizedIndex)) return;
        if (!isSectionInViewport()) return;

        const content = contentData[normalizedIndex];
        if (!content) return;

        eventTrackingOnView(content);
        trackedSlideIndexesRef.current.add(normalizedIndex);
    };

    const topOfferTrackingKey = contentData
        ?.map((content) => content?.id ?? content?.mobImage ?? content?.webImage)
        .join("|");

    useEffect(() => {
        if (!contentData?.length) return;

        trackedSlideIndexesRef.current = new Set();
        activeSlideRef.current = 0;

        const checkVisibility = () => {
            triggerViewEventForSlide(activeSlideRef.current);
        };

        // Check immediately after render in case section is already in viewport
        const timer = setTimeout(() => checkVisibility(), 150);

        window.addEventListener('scroll', checkVisibility, { passive: true });
        return () => {
            clearTimeout(timer);
            window.removeEventListener('scroll', checkVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topOfferTrackingKey]);

    const handleClick = (cta, contentData) => {
        let bannerName = getImageName(contentData?.mobImage);
        trackEvent("select_promotion", {
            client_code: clientCode,
            platform_code: platformCode,
            user_id: userId,
            user_status: userStatus,
            card_name: defaultCardName,
            page_type: window.location.pathname === "/" ? "home_page" : "my_account_page",
            ecommerce: {
                creative_name: bannerName, // ex: Name of the featured banner 
                creative_slot: `${sectionData?.displayOrder}-${contentData?.displayOrder}`,   //The position of the banner in the grid ex: 1-1,1-2 
                promotion_id: String(contentData?.id),  // Unique Banner id for displayed banner
                promotion_name: sectionData?.text || sectionData?.sectionName // Unique Banner title of displayed banner; ex: Featured for you
            }
        });
        const { isStaccOffer, disclaimerContent } = contentData;
        if (!isStaccOffer) {
            if (cta && disclaimerContent) {
                setBannerData(contentData);
                setDisclaimerPopup(true);
            }
            return;
        }

        if (cta) {
            handleRedirectWithLoading(
                setIsLoading,
                cta,
                login,
                setShowModal,
                isModal,
                navigate,
                isHomePage ? loginUrl : cta
            );
        }
    };

    const settings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 4000,
        arrows: false,
        appendDots: (dots) => (
            <div className="carousel-dots">
                <ul>{dots}</ul>
            </div>
        ),
        customPaging: () => <div className="dot"></div>,
        afterChange: (currentSlide) => {
            activeSlideRef.current = currentSlide;
            triggerViewEventForSlide(currentSlide);
        },
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    autoplay: true,
                },
            },
        ],
    };

    return (
        <div className="mt-4" ref={sectionRef}>
            {contentData.length === 1 && contentData[0]?.mobImage ? (
                <div
                    className={` mt-0 pb-4 relative ${isFullWidth ? "px-0" : "px-4 container mx-auto"
                        }`}
                >
                    <img
                        onClick={() => handleClick(contentData[0]?.cta, contentData[0])}
                        src={GetImageUrl(contentData[0]?.mobImage)}
                        alt="banner-single"
                        className={`w-full cursor-pointer ${!isFullWidth ? "rounded-2xl" : ""
                            }`}
                    />
                </div>
            ) : (
                ""
            )}

            {contentData.length > 1 ? (
                <div
                    className={`container mx-auto mt-0 pb-0 relative ${isFullWidth ? "px-0" : "px-3"
                        }`}
                >
                    <Slider {...settings} ref={sliderRef}>
                        {contentData.map((conData, index) => (
                            <div key={index} className="px-1">
                                {conData?.mobImage && <img
                                    onClick={() => handleClick(conData?.cta, conData)}
                                    src={GetImageUrl(conData?.mobImage)}
                                    alt={`banner-${index}`}
                                    className={`w-full cursor-pointer ${!isFullWidth ? "rounded-2xl" : ""
                                        }`}
                                />}
                            </div>
                        ))}
                    </Slider>
                </div>
            ) : (
                ""
            )}
            {disclaimerPopup && (
                <>
                    <DisclaimerPopup
                        onClose={() => setDisclaimerPopup(false)}
                        isOpen={disclaimerPopup}
                        bannerData={bannerData}
                        isMobileView={isMobileView}
                    />
                </>
            )}
        </div>
    );
};

export default TopOfferBanner;
